import { Component, input } from '@angular/core';
import { LucideChevronDown, LucideDynamicIcon } from '@lucide/angular';
import { NgpAccordionContent, NgpAccordionItem, NgpAccordionTrigger } from 'ng-primitives/accordion';
import { NgpButton } from 'ng-primitives/button';
import { InlineButtonComponent } from '../../atoms/inline-button/inline-button.component';
import { TextDirective } from '../../typography/text.directive';
import { JoiiIcon } from '../../icons/lucide/icon';

@Component({
  selector: 'joii-accordion-item',
  hostDirectives: [
    {
      directive: NgpAccordionItem,
      inputs: ['ngpAccordionItemValue:value', 'ngpAccordionItemDisabled:disabled'],
    },
  ],
  imports: [
    NgpAccordionContent,
    NgpAccordionTrigger,
    NgpButton,
    LucideDynamicIcon,
    InlineButtonComponent,
    TextDirective,
  ],
  template: `
    <button
      joii-inline-button
      [borderless]="true"
      [fullWidth]="true"
      variant="secondary"
      ngpAccordionTrigger
      [suffixIcon]="chevronDownIcon"
    >
      <span class="accordion-item-content">
        @if (icon(); as icon) {
          <svg [lucideIcon]="icon" [size]="24" />
        }
        <span>{{ heading() }}</span>
      </span>
    </button>
    <div ngpAccordionContent joii-text [color]="'tint2'" [size]="3">
      <ng-content />
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    :host:has(+ :host) {
      border-bottom: 1px solid var(--Vetai-Form_controls--Border_Default);
    }

    .accordion-item-content {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-2);
    }

    [ngpAccordionTrigger][data-variant='secondary'] {
      color: var(--Vetai-Form_controls--Text_Default);
      justify-content: space-between;
    }

    [ngpAccordionTrigger][data-variant='secondary'][data-open] {
      border-bottom: 1px solid var(--Vetai-Form_controls--Border_Default);
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    [ngpAccordionTrigger][data-variant='secondary'][data-hover],
    [ngpAccordionTrigger][data-variant='secondary'][data-focus-visible] {
      border: 1px solid var(--Vetai-Form_controls--Border_Hover);
    }

    [ngpAccordionContent] {
      overflow: hidden;
      box-sizing: border-box;
    }

    [ngpAccordionContent][data-open] {
      animation: slideDown 0.2s ease-in-out forwards;
    }

    [ngpAccordionContent][data-closed] {
      animation: slideUp 0.2s ease-in-out forwards;
    }
    /* Chevron is inside inline-button; pierce encapsulation so the rotation applies when open */
    :host ::ng-deep [ngpAccordionTrigger] svg.lucide:last-of-type {
      transition: transform 0.2s ease;
    }
    :host ::ng-deep [ngpAccordionTrigger][data-open] svg.lucide:last-of-type {
      transform: rotate(180deg);
    }

    @keyframes slideDown {
      from {
        height: 0;
      }
      to {
        height: var(--ngp-accordion-content-height);
      }
    }

    @keyframes slideUp {
      from {
        height: var(--ngp-accordion-content-height);
      }
      to {
        height: 0;
      }
    }
  `,
})
export class AccordionItemComponent {
  readonly heading = input.required<string>();
  readonly icon = input<JoiiIcon>();
  protected readonly chevronDownIcon = LucideChevronDown;
}
